import { Ref, useCallback, useEffect, useState } from 'react';
import ResizeObserver from 'resize-observer-polyfill';
import shallowEqual from 'shallowequal';

import { ImMNElement } from '../../../../common/elements/elementModelTypes';
import { currentlyEditingThisEditor } from '../../../components/editor/milanoteEditor/editorPropComparisons';
import { multiRef } from '../../../utils/multiRef';
import {
    getCardDimensionPropertiesPx,
    getIsAutoHeight,
    getIsEditorOverThreshold,
    isHeightRestricted,
} from './resizableEditorUtils';

export interface EditorOverflowObserverProps {
    editorRef: Ref<HTMLDivElement>;
    containerRef: Ref<HTMLDivElement>;
    isEditing: boolean;
    isResizing: boolean;
    gridSize: number;
    element: ImMNElement;
    currentEditorKey: string;
    targetEditorKey: string;
    getContextZoomScale: () => number;
}

type EditorOverflowObserverReturn = {
    editorOverflowed: boolean;
    editorRef: Ref<HTMLDivElement>;
    containerRef: Ref<HTMLDivElement>;
    isEditingTargetContent: boolean;
    recalculateOverflow: () => void;
};

const useEditorOverflowObserver = (
    props: EditorOverflowObserverProps,
    targetEditorKeys: string[],
    paddingGu = 0,
): EditorOverflowObserverReturn => {
    const {
        isResizing,
        gridSize,
        element,
        isEditing,
        currentEditorKey,
        editorRef,
        containerRef,
        getContextZoomScale = () => 1,
    } = props;
    const [isOverflowed, setIsOverflowed] = useState(false);

    const zoomScale = getContextZoomScale();

    // these are implemented as state rather than refs because we
    // need to trigger the useEffects when they change
    const [containerHtmlElement, setContainerHtmlElement] = useState<HTMLElement | null>(null);
    const [editorHtmlElement, setEditorHtmlElement] = useState<HTMLElement | null>(null);

    // Track the heights as a single object - with two useState(0)'s, we'd need to set them individually,
    // and let their dependant useEffects run unnecessarily (and inaccurately) in between.
    const [cachedHeights, setCachedHeightsInner] = useState({ editorHeight: 0, containerHeight: 0 });
    const setCachedHeights = useCallback((editorHeight: number | undefined, containerHeight: number | undefined) => {
        setCachedHeightsInner((cached) => {
            const updated = {
                editorHeight: editorHeight || cached.editorHeight,
                containerHeight: containerHeight || cached.containerHeight,
            };
            if (shallowEqual(updated, cached)) return cached;
            return updated;
        });
    }, []);

    // Read heights immediately on mount, and whenever the user starts/stops editing
    const currentlyEditing = currentlyEditingThisEditor(props);
    const heightRestricted = isHeightRestricted(element);
    const { maxHeight } = getCardDimensionPropertiesPx({ element, gridSize }) || 0;
    useEffect(() => {
        if (!editorHtmlElement) return;
        if (!heightRestricted) return;

        const editorHeight = editorHtmlElement.getBoundingClientRect().height / zoomScale;
        setCachedHeights(editorHeight, maxHeight);
    }, [maxHeight, gridSize, editorHtmlElement, currentlyEditing, heightRestricted]);

    // Attach a resize observer during the resize action
    useEffect(() => {
        if (!editorHtmlElement || !containerHtmlElement || !isResizing) return;

        const observer = new ResizeObserver((entries) => {
            const editorEntry = entries.find((entry) => entry.target === editorHtmlElement);
            const containerEntry = entries.find((entry) => entry.target === containerHtmlElement);
            requestAnimationFrame(() => {
                setCachedHeights(editorEntry?.contentRect.height, containerEntry?.contentRect.height);
            });
        });

        observer.observe(editorHtmlElement);
        observer.observe(containerHtmlElement);

        return () => observer.disconnect();
    }, [isResizing, containerHtmlElement, editorHtmlElement]);

    // Update the overflow status whenever the recorded heights change
    useEffect(() => {
        const { containerHeight, editorHeight } = cachedHeights;
        if (!containerHeight || !editorHeight) return;

        const isOverThreshold = getIsEditorOverThreshold(containerHeight, editorHeight, paddingGu, gridSize);
        setIsOverflowed(isOverThreshold);
    }, [cachedHeights, gridSize]);

    // Determine whether to actually show the overflow display
    const isEditingTargetContent = isEditing && targetEditorKeys.includes(currentEditorKey);
    const isAutoHeight = getIsAutoHeight(element);
    const showEditorOverflow = (() => {
        if (!isOverflowed) return false;
        if (isResizing) return true;
        if (isEditingTargetContent) return false;
        if (isAutoHeight) return false;
        return true;
    })();

    const recalculateOverflow = () => {
        if (!editorHtmlElement || !containerHtmlElement) return;

        const editorHeight = editorHtmlElement?.getBoundingClientRect().height || 0;
        const containerHeight = containerHtmlElement?.getBoundingClientRect().height || 0;
        setCachedHeights(editorHeight, containerHeight);
    };

    return {
        editorOverflowed: showEditorOverflow,
        editorRef: multiRef([setEditorHtmlElement, editorRef]),
        containerRef: multiRef([setContainerHtmlElement, containerRef]),
        isEditingTargetContent,
        recalculateOverflow,
    };
};

export default useEditorOverflowObserver;
